import { ref } from 'vue';
import type { DataTableFilterMeta, DataTableFilterMetaData, DataTableOperatorFilterMetaData } from 'primevue/datatable';

export type Filters = DataTableFilterMeta | null;

export interface SortConfig {
  field: string | ((item: unknown) => string);
  direction: 'asc' | 'desc';
}

export interface DataTableConfig {
  sort: SortConfig;
  page: number;
  filters: Filters;
  perPage: number;
  dataKey: string;
}

export interface Meta {
  from: number;
  to: number;
  total: number;
  perPage: number;
  count: number;
  lastPage: number;
}

export interface DataTableData<T = unknown> {
  items: T[];
  meta?: Meta;
}

export const useDataTable = <T = unknown>() => {
  const createDefaultConfig = (): DataTableConfig => ({
    sort: { field: '', direction: 'desc' },
    page: 1,
    filters: null,
    perPage: 25,
    dataKey: 'id',
  });

  const config = ref<DataTableConfig>(createDefaultConfig());

  const data = ref<DataTableData<T> | null>(null);

  const setData = (backendData: DataTableData<T>): void => {
    data.value = backendData;
  };

  const setDataKey = (key: string): void => {
    config.value.dataKey = key || 'id';
  };

  const getApiConfig = (apiConfig: DataTableConfig): Record<string, unknown> => {
    const params: Record<string, DataTableFilterMetaData | DataTableOperatorFilterMetaData | number | boolean | string> = {
      page: apiConfig.page,
      perPage: apiConfig.perPage,
    };

    if (apiConfig.sort.field) {
      params[`order[${apiConfig.sort.field}]`] = apiConfig.sort.direction;
    }

    if (apiConfig.filters) {
      // eslint-disable-next-line no-restricted-syntax
      for (const [ key, value ] of Object.entries(apiConfig.filters)) {
        params[key] = value;
      }
    }

    return params;
  };

  const reset = (): void => {
    config.value = { ...createDefaultConfig() };
    data.value = null;
  };

  return {
    config,
    data,
    setData,
    getApiConfig,
    reset,
    setDataKey,
  };
};
